import { useState } from "react";
import { useTranslation } from "react-i18next";
import { ApiError } from "@/lib/api";
import {
  type BroadcastMessage,
  type BroadcastSchedule,
  DEFAULT_SCHEDULE,
  countChatLines,
  joinDays,
  useCreateBroadcast,
} from "@/lib/broadcasts";
import { pushToast } from "@/components/Toaster";
import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/cn";
import { AccountPicker } from "./AccountPicker";
import { MessagesEditor } from "./MessagesEditor";
import { ScheduleFields } from "./ScheduleFields";

const STEPS = ["what", "where", "who", "how"] as const;

const FIELD =
  "w-full rounded-lg border border-border bg-bg-primary px-3 py-2 font-mono text-sm leading-relaxed " +
  "placeholder:font-sans placeholder:text-fg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent";

/**
 * A new broadcast in four questions: what to post, where, from which accounts,
 * how often. Only the name is required — the rest can be added from the
 * broadcast's card, and it starts posting by itself once it has all three.
 */
export function CreateBroadcastDialog({
  open,
  onOpenChange,
  onCreated,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated: (id: string) => void;
}) {
  const { t } = useTranslation();
  const create = useCreateBroadcast();
  const [step, setStep] = useState(0);
  const [name, setName] = useState("");
  const [messages, setMessages] = useState<BroadcastMessage[]>([]);
  const [chatsText, setChatsText] = useState("");
  const [accountIds, setAccountIds] = useState<string[]>([]);
  const [schedule, setSchedule] = useState<BroadcastSchedule>(DEFAULT_SCHEDULE);
  const [error, setError] = useState<string | null>(null);

  const chats = countChatLines(chatsText);
  const last = step === STEPS.length - 1;

  const close = (next: boolean) => {
    if (!next) {
      setStep(0);
      setName("");
      setMessages([]);
      setChatsText("");
      setAccountIds([]);
      setSchedule(DEFAULT_SCHEDULE);
      setError(null);
    }
    onOpenChange(next);
  };

  const next = () => {
    if (step === 0 && !name.trim()) {
      setError(t("broadcasts.create.name_required"));
      return;
    }
    setError(null);
    setStep((s) => s + 1);
  };

  const submit = async () => {
    setError(null);
    try {
      const created = await create.mutateAsync({
        name: name.trim(),
        ...schedule,
        material_ids: messages.map((m) => m.id),
        account_ids: accountIds,
        chats_text: chatsText,
      });
      pushToast({
        kind: "success",
        text: t(
          created.waiting_reason === null || created.waiting_reason === "joining"
            ? "broadcasts.create.done"
            : "broadcasts.create.done_incomplete",
        ),
      });
      close(false);
      onCreated(created.id);
    } catch (err) {
      setError(err instanceof ApiError ? err.detail : t("broadcasts.create.error"));
    }
  };

  // What is still missing, said on the last step: the broadcast is created
  // anyway, but it will not post until it has all three.
  const missing = [
    messages.length === 0 && t("broadcasts.create.missing_messages"),
    chats === 0 && t("broadcasts.create.missing_chats"),
    accountIds.length === 0 && t("broadcasts.create.missing_accounts"),
  ].filter(Boolean) as string[];

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent size="lg">
        <DialogHeader>
          <DialogTitle>{t("broadcasts.create.title")}</DialogTitle>
          <DialogDescription>{t(`broadcasts.create.step_${STEPS[step]}_title`)}</DialogDescription>
          <Stepper current={step} />
        </DialogHeader>

        <DialogBody className="space-y-5">
          {step === 0 && (
            <>
              <div className="space-y-2">
                <Label htmlFor="bc_name">{t("broadcasts.field.name")}</Label>
                <Input
                  id="bc_name"
                  value={name}
                  onChange={(e) => {
                    setName(e.target.value);
                    setError(null);
                  }}
                  maxLength={128}
                  autoFocus
                  placeholder={t("broadcasts.field.name_placeholder") ?? ""}
                />
              </div>
              <div className="space-y-2">
                <Label>{t("broadcasts.field.messages")}</Label>
                <MessagesEditor
                  messages={messages}
                  onAdd={(m) => setMessages((list) => [...list, m])}
                  onRemove={(id) => setMessages((list) => list.filter((m) => m.id !== id))}
                />
              </div>
            </>
          )}

          {step === 1 && (
            <div className="space-y-2">
              <Label htmlFor="bc_chats">{t("broadcasts.field.chats")}</Label>
              <textarea
                id="bc_chats"
                value={chatsText}
                onChange={(e) => setChatsText(e.target.value)}
                rows={10}
                placeholder={"https://t.me/job_board\n@another_board\nhttps://t.me/+AbCdEfGhIjKl"}
                className={cn(FIELD, "min-h-[12rem] resize-y")}
              />
              <p className="text-xs text-fg-muted">{t("broadcasts.chats.paste_hint")}</p>
              <p className="text-sm text-fg-secondary">
                {t("broadcasts.chats.recognised", { count: chats })}
                {chats > 0 && (
                  <span className="text-fg-muted">
                    {" · "}
                    {t("broadcasts.chats.join_eta", { count: joinDays(chats) })}
                  </span>
                )}
              </p>
              <Alert tone="info">{t("broadcasts.chats.join_note")}</Alert>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-3">
              <Label>{t("broadcasts.field.accounts")}</Label>
              <AccountPicker selected={accountIds} onChange={setAccountIds} />
              <p className="text-xs leading-relaxed text-fg-muted">
                {t("broadcasts.accounts.gate_hint")}
              </p>
            </div>
          )}

          {step === 3 && (
            <>
              <ScheduleFields
                value={schedule}
                onChange={setSchedule}
                chats={chats}
                accounts={accountIds.length}
              />
              {missing.length > 0 && (
                <Alert tone="warning" title={t("broadcasts.create.missing_title")}>
                  {missing.join(" · ")}
                </Alert>
              )}
            </>
          )}

          {error && <Alert tone="danger">{error}</Alert>}
        </DialogBody>

        <DialogFooter>
          <span className="mr-auto text-xs text-fg-muted">
            {t("broadcasts.create.step_of", { current: step + 1, total: STEPS.length })}
          </span>
          {step > 0 && (
            <Button variant="outline" onClick={() => setStep((s) => s - 1)}>
              {t("broadcasts.create.back")}
            </Button>
          )}
          {last ? (
            <Button loading={create.isPending} onClick={submit}>
              {t("broadcasts.create.submit")}
            </Button>
          ) : (
            <Button onClick={next}>{t("broadcasts.create.next")}</Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
CreateBroadcastDialog.displayName = "CreateBroadcastDialog";

function Stepper({ current }: { current: number }) {
  const { t } = useTranslation();
  return (
    <ol className="mt-3 flex items-center gap-2">
      {STEPS.map((key, i) => {
        const active = i === current;
        const done = i < current;
        return (
          <li
            key={key}
            className={cn(
              "flex min-w-0 items-center gap-2",
              active ? "flex-[2_1_0%] sm:flex-1" : "flex-1",
            )}
          >
            <span
              className={cn(
                "flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs font-semibold",
                active
                  ? "bg-accent text-fg-on-accent"
                  : done
                    ? "bg-success text-fg-on-accent"
                    : "bg-bg-elevated text-fg-muted",
              )}
            >
              {i + 1}
            </span>
            <span
              className={cn(
                "truncate text-xs",
                active ? "font-medium text-fg-primary" : "hidden text-fg-muted sm:inline",
              )}
            >
              {t(`broadcasts.create.step_${key}`)}
            </span>
            {i < STEPS.length - 1 && <span className="h-px flex-1 bg-border" aria-hidden="true" />}
          </li>
        );
      })}
    </ol>
  );
}
